import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbDataValueUtils } from "abreact/data/AbDataValueUtils";
import { isCollectionAccessor, isScalarAccessor } from "abreact/data/DataValueAccessor";
import { DataValueViews } from "abreact/data/DataValueViews";
import { AbField } from "abreact/form/AbField";
import { AbFieldDecoratorFactory } from "abreact/form/AbFieldDecoratorFactory";
import { AbFieldGroup } from "abreact/form/AbFieldGroup";
import { AbUnsetValue } from "abreact/form/AbUnsetValue";
import { ValidationSeverity } from "abreact/form/ValidationIssue";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { defaultItemDisplayStringFunction } from "abreact/incubator/ui/form/AbInputUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import * as React from "react";
export class DataValueFieldFactory {
  static fieldProps(componentName, isEditing, isInEditMode, validationIssues, props, decoratorsFunction) {
    const isModified = AbDataValueUtils.isDataValueModified(props.subject, props.accessor);
    const decoratorProps = {
      ...props,
      isInEditMode: isInEditMode
    };
    return {
      ...ReactUtils.inputProps(componentName, props, [!isEditing && "ab-is-read-only", isModified && "ab-is-modified"]),
      id: props.id,
      label: props.label !== undefined ? props.label : props.accessor.getDynamicDisplayName ? props.accessor.getDynamicDisplayName(props.subject) : props.accessor.displayName,
      autoLabelPlacement: props.autoLabelPlacement,
      stackLabel: props.stackLabel,
      dataTestId: props.dataTestId ?? (props.accessor.name ? `ab-field.${props.accessor.name}` : undefined),
      labelDataTestId: props.labelDataTestId ?? (props.accessor.name ? `ab-field.${props.accessor.name}.label` : undefined),
      required: props.accessor.constraints?.required,
      modified: isModified,
      readOnly: props.readOnly || !isEditing,
      standalone: props.standalone,
      fullWidth: props.fullWidth,
      description: props.accessor.description,
      tooltip: props.tooltip,
      decorators: props.decorators || (decoratorsFunction ? decoratorsFunction(decoratorProps, validationIssues) : DataValueFieldFactory.createDataValueFieldDecorators(decoratorProps, validationIssues)),
      menuItems: props.menuItems
    };
  }
  static renderDataValueField(props) {
    const viewProvider = DataValueViews.getViewProvider(props.accessor.dataType);
    if (!viewProvider.renderField) {
      throw new Error(`Unable to render a field for data type ${props.accessor.dataType}`);
    }
    return viewProvider.renderField(props);
  }
  static createDataValueFieldDecorators(props, validationIssues) {
    const subject = props.subject;
    const accessor = props.accessor;
    const cannotEditReason = subject && props.isInEditMode && accessor.cannotEditReason ? accessor.cannotEditReason(subject) : undefined;
    const changeDecorator = props.changeDecorator === undefined ? DataValueFieldFactory.createDataValueChangeDecorator(props) : props.changeDecorator;
    const canRevert = subject && !props.readOnly && props.isInEditMode && AbDataValueUtils.canRevertDataValue(subject, accessor);
    return [...(changeDecorator ? [changeDecorator] : []), ...AbFieldDecoratorFactory.createFieldDecorators({
      readOnly: props.readOnly,
      disabled: props.disabled,
      validationIssues: validationIssues,
      cannotEditReason: cannotEditReason,
      revertFunction: canRevert && subject ? () => {
        const originalSubject = AbDataValueUtils.revertDataValue(subject, accessor);
        if (originalSubject !== undefined) {
          const currentValue = DataValueFieldFactory.getCurrentValue(originalSubject, accessor);
          props.onRevert ? props.onRevert(originalSubject, currentValue) : props.onChange && props.onChange(originalSubject, currentValue);
        }
      } : undefined
    })];
  }
  static performValidation(props) {
    const validationIssues = [];
    try {
      if (props.subject) {
        const currentValue = DataValueFieldFactory.getCurrentValue(props.subject, props.accessor, props.currentValue);
        if (isScalarAccessor(props.accessor)) {
          if (props.accessor.validate) {
            validationIssues.push(...props.accessor.validate(props.subject, currentValue));
          } else {
            if (props.accessor.constraints?.required && currentValue == null) {
              validationIssues.push({
                severity: ValidationSeverity.ERROR,
                message: AbReactResources.field_attributeRequiredError(props.accessor.displayName)
              });
            }
          }
        }
        if (isCollectionAccessor(props.accessor)) {
          if (props.accessor.validate) {
            validationIssues.push(...props.accessor.validate(props.subject, currentValue));
          }
        }
      }
    } catch (error) {
      validationIssues.push({
        severity: ValidationSeverity.INTERNAL_ERROR,
        message: AbReactResources.field_unexpectedValidationError(),
        internalError: error
      });
    }
    return validationIssues;
  }
  static getCurrentValue(subject, accessor, currentValue) {
    if (currentValue !== undefined) {
      return currentValue;
    }
    if (subject === undefined) {
      return null;
    }
    if (isScalarAccessor(accessor)) {
      return accessor.getClientValue(subject);
    } else if (isCollectionAccessor(accessor)) {
      return AbDataValueUtils.getClientItems(subject, accessor);
    }
    throw new Error(`Unable to determine current value for accessor "${accessor.displayName}"`);
  }
  static createDataValueChangeDecorator(props) {
    if (props.subject && (props.readOnly || !props.isInEditMode) && isScalarAccessor(props.accessor) && !(props.accessor.isClientOnly?.(props.subject) ?? false) && (props.accessor.hasClientChange && props.accessor.hasClientChange(props.subject) || props.accessor.hasServerChange && props.accessor.hasServerChange(props.subject))) {
      const cannotEdit = props.accessor.cannotEdit ? props.accessor.cannotEdit(props.subject) : false;
      return {
        className: "change-decorator",
        icon: cannotEdit ? "absdk:modified-locked" : "absdk:modified",
        tooltip: DataValueFieldFactory.renderScalarChangeTooltip(props.subject, props.accessor)
      };
    }
    return undefined;
  }
  static renderScalarChangeTooltip(subject, accessor) {
    const serverValue = accessor.getServerValue ? accessor.getServerValue(subject) : undefined;
    const baselineValue = accessor.getBaselineValue ? accessor.getBaselineValue(subject) : undefined;
    const viewProvider = DataValueViews.getViewProvider(accessor.dataType);
    const serverValueName = baselineValue !== undefined ? AbReactResources.field_changeSetValueName() : AbReactResources.field_serverValueName();
    return /*#__PURE__*/React.createElement(AbFieldGroup, {
      compact: true,
      style: {
        width: "300px"
      }
    }, accessor.hasClientChange && accessor.hasClientChange(subject) && /*#__PURE__*/React.createElement(AbField, {
      label: serverValueName,
      readOnly: true
    }, viewProvider.renderValue({
      subject: subject,
      accessor: accessor,
      // TODO AndyA: can we avoid this cast?
      currentValue: serverValue
    }) ?? /*#__PURE__*/React.createElement(AbUnsetValue, null)), baselineValue !== undefined && /*#__PURE__*/React.createElement(AbField, {
      label: AbReactResources.field_baselineValueName(),
      readOnly: true
    }, viewProvider.renderValue({
      subject: subject,
      accessor: accessor,
      // TODO AndyA: can we avoid this cast?
      currentValue: baselineValue
    }) ?? /*#__PURE__*/React.createElement(AbUnsetValue, null)));
  }
  static getItemDisplayName(accessor, item) {
    if (accessor.getItemDisplayName) {
      return accessor.getItemDisplayName(item);
    } else {
      return defaultItemDisplayStringFunction(item);
    }
  }
  static createEditableItemContext(props) {
    const subject = props.subject;
    const isEditable = subject !== undefined && !props.readOnly && AbDataValueUtils.canModifyAccessor(props.accessor) && (props.accessor.cannotEdit ? !props.accessor.cannotEdit(subject) : true);
    const changeValue = (currentSubject, newValue) => {
      if (isScalarAccessor(props.accessor) && props.accessor.setClientValue) {
        const newSubject = props.accessor.setClientValue(currentSubject, newValue);
        props.onChange && props.onChange(newSubject, props.accessor.getClientValue(newSubject));
        return newSubject;
      } else if (isCollectionAccessor(props.accessor)) {
        const newSubject = AbDataValueUtils.setClientItems(currentSubject, props.accessor, newValue ?? []);
        props.onChange && props.onChange(newSubject, newValue);
        return newSubject;
      } else {
        throw new Error(`Unable to update accessor "${props.accessor.displayName}"`);
      }
    };
    const canRevert = subject && props.accessor.revertClientChange && props.accessor.canRevertClientChange && props.accessor.canRevertClientChange(subject);
    const revertFunction = canRevert && subject ? currentSubject => {
      if (props.accessor.revertClientChange && props.accessor.canRevertClientChange && props.accessor.canRevertClientChange(currentSubject)) {
        const originalSubject = props.accessor.revertClientChange(currentSubject);
        props.onRevert && props.onRevert(originalSubject, DataValueFieldFactory.getCurrentValue(originalSubject, props.accessor));
        return originalSubject;
      } else {
        return subject;
      }
    } : undefined;
    return {
      subject: subject,
      editManager: props.editManager,
      isEditable: isEditable,
      createEditableItem: () => {
        return new DataValueEditableItem(subject, props.accessor);
      },
      changeValue: changeValue,
      isModified: currentSubject => {
        return props.accessor.hasClientChange ? props.accessor.hasClientChange(currentSubject) : true;
      },
      revertChange: revertFunction
    };
  }
}
class DataValueEditableItem {
  constructor(subject, accessor) {
    _defineProperty(this, "subject", void 0);
    _defineProperty(this, "accessor", void 0);
    this.subject = subject;
    this.accessor = accessor;
  }
  validationIssues() {
    const currentValue = DataValueFieldFactory.getCurrentValue(this.subject, this.accessor);
    return this.subject && this.accessor.validate ? this.accessor.validate(this.subject, currentValue) : [];
  }
  revertEditing() {
    if (this.subject && this.accessor.hasClientChange && this.accessor.hasClientChange(this.subject)) {
      this.accessor.revertClientChange && this.accessor.revertClientChange(this.subject);
    }
  }
  saveEditing() {
    return true;
  }
  startEditing() {
    // There's nothing to do here
  }
}
//# sourceMappingURL=DataValueFieldFactory.js.map
